import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import CookieBanner from "@/components/site/CookieBanner";
import { AuthProvider } from "@/context/AuthContext";

// Route-level code splitting: heavy pages ship in their own chunk only when needed.
const Configurator = lazy(() => import("@/pages/Configurator"));
const ConfrontoPreventivi = lazy(() => import("@/pages/ConfrontoPreventivi"));
const GuideAccumulo = lazy(() => import("@/pages/GuideAccumulo"));
const AnalisiPreventivo = lazy(() => import("@/pages/AnalisiPreventivo"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Cookie = lazy(() => import("@/pages/CookiePolicy"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const NotFound = lazy(() => import("@/pages/NotFound"));
// The AI assistant lives at the bottom of every page but isn't LCP-critical.
const ChatAssistant = lazy(() => import("@/components/ai/ChatAssistant"));

/** Minimal transparent fallback — prevents CLS while a route chunk loads. */
const RouteFallback = () => <div className="min-h-[70vh]" aria-hidden="true" />;

/** Keep hash targets aligned while asynchronous content above them loads. */
function ScrollToHash() {
  const { pathname, hash, key } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
      return;
    }
    let id;
    try { id = decodeURIComponent(hash.slice(1)); } catch { return; }
    let stopped = false;
    let frame = 0;
    let retry = 0;
    let expiry = 0;
    const align = () => {
      if (stopped) return;
      const el = document.getElementById(id);
      if (!el) {
        retry = window.setTimeout(align, 100);
        return;
      }
      const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - 80);
      if (Math.abs(window.scrollY - top) > 1) window.scrollTo({ top, behavior: "instant" });
    };
    const observer = new ResizeObserver(() => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(align);
    });
    const stop = () => {
      stopped = true;
      observer.disconnect();
      window.cancelAnimationFrame(frame);
      window.clearTimeout(retry);
      window.clearTimeout(expiry);
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
      window.removeEventListener("pointerdown", stop);
      window.removeEventListener("keydown", stopOnKey);
    };
    const stopOnKey = (event) => {
      if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " ", "Tab", "Escape"].includes(event.key)) stop();
    };
    // User interaction always wins; never pull a reader back after they move on.
    window.addEventListener("wheel", stop, { passive: true });
    window.addEventListener("touchstart", stop, { passive: true });
    window.addEventListener("pointerdown", stop, { passive: true });
    window.addEventListener("keydown", stopOnKey);
    observer.observe(document.body);
    frame = window.requestAnimationFrame(align);
    expiry = window.setTimeout(stop, 6000);
    return stop;
  }, [pathname, hash, key]);
  return null;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <ScrollToHash />
            <Header />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/guide/confrontare-preventivi-fotovoltaico" element={<ConfrontoPreventivi />} />
                <Route path="/guide/accumulo-fotovoltaico-quando-conviene" element={<GuideAccumulo />} />
                <Route path="/analisi-preventivo" element={<AnalisiPreventivo />} />
                <Route path="/configuratore" element={<Configurator />} />
                <Route path="/configuratore/:tipo" element={<Configurator />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookie" element={<Cookie />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <Footer />
            <CookieBanner />
            <Suspense fallback={null}>
              <ChatAssistant />
            </Suspense>
            <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
